import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useModuleSettings } from '../../context/adminSettingsContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { hasPermission } from '../../common/permissions';
import { allTutors, approveRejectTutors, assignSubscription, changeTutorMohreStatus, changeTutorStatus, deleteTutors } from '../../slice/tutorSlice';
import Swal from 'sweetalert2';
import moment from 'moment';
import DataTable from 'react-data-table-component';

const TutorList = () => {

    const dispatch = useDispatch();
    const { tutorList } = useSelector(state => state.tutor);

    const navigate = useNavigate();
    const location = useLocation();
    const { status } = location.state || {};

    const { page, setPage, limit, setLimit, filter: statusFilter, setFilter: setStatusFilter, search: searchQuery, setSearch: setSearchQuery } = useModuleSettings('tutors', { filter: status || 'active' })

    const [searchDebounce, setSearchDebounce] = useState(searchQuery)
    const isMounted = useRef(false)

    // Subscription modal state
    const [showSubModal, setShowSubModal] = useState(false)
    const [selectedTutor, setSelectedTutor] = useState(null)
    const [subEndDate, setSubEndDate] = useState('')
    const [subDateError, setSubDateError] = useState('')
    const [subLoading, setSubLoading] = useState(false)

    // ── Helpers ───────────────────────────────────────────────────────────────
    const isTutorSubscribed = (row) => Number(row?.is_premium_verified) === 1
    const isManualPlan = (row) => row?.subscription_type === 'manual'

    const refreshList = () => {
        dispatch(allTutors({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined,
        }))
    }

    // ── Subscription modal ────────────────────────────────────────────────────
    const openSubModal = (tutor) => {
        setSelectedTutor(tutor)
        setSubEndDate(
            tutor.subscription_end_date
                ? moment(tutor.subscription_end_date).format('YYYY-MM-DDTHH:mm')
                : ''
        )
        setSubDateError('')
        setShowSubModal(true)
    }

    const closeSubModal = () => {
        setShowSubModal(false)
        setSelectedTutor(null)
        setSubEndDate('')
        setSubDateError('')
    }

    const handleDateChange = (val) => {
        setSubEndDate(val)
        if (val && moment(val).isBefore(moment())) {
            setSubDateError('End date/time cannot be in the past')
        } else {
            setSubDateError('')
        }
    }

    const handleSubscriptionSubmit = async () => {
        if (!subEndDate) {
            Swal.fire({
                toast: true, position: 'top-end', icon: 'warning', backdrop: false,
                title: 'Please select a subscription end date',
                showConfirmButton: false, timer: 2000,
            })
            return
        }

        if (moment(subEndDate).isBefore(moment())) {
            Swal.fire({
                toast: true, position: 'top-end', icon: 'error', backdrop: false,
                title: 'End date/time cannot be in the past',
                showConfirmButton: false, timer: 2000,
            })
            setSubDateError('End date/time cannot be in the past')
            return
        }

        setSubLoading(true)

        try {
            const utcEndDate = moment(subEndDate).toISOString()

            const response = await dispatch(assignSubscription({
                tutor_id: selectedTutor.id,
                end_date: utcEndDate,
                subscription_id: selectedTutor.subscription_id ?? null,
                subscription_type: selectedTutor.subscription_type ?? null,
                is_update: isTutorSubscribed(selectedTutor) ? 1 : 0,
            }))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    toast: true, position: 'top-end', icon: 'success', backdrop: false,
                    title: isTutorSubscribed(selectedTutor)
                        ? 'Subscription updated successfully'
                        : 'Subscription assigned successfully',
                    showConfirmButton: false, timer: 1500,
                })
                closeSubModal()
                refreshList()
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: response?.payload?.message || 'Failed to process subscription.',
                })
            }
        } catch (error) {
            Swal.fire({
                icon: 'error', title: 'Failed',
                text: 'Failed to assign subscription.',
            })
        } finally {
            setSubLoading(false)
        }
    }

    // ── Effects ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true
            setSearchDebounce(searchQuery)
            return
        }
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    useEffect(() => {
        refreshList()
    }, [dispatch, page, limit, statusFilter, searchDebounce])

    // ── Action handlers ───────────────────────────────────────────────────────
    const handleToggleStatus = async (tutorId) => {
        try {
            const response = await dispatch(changeTutorStatus({ id: tutorId }))
            if (response?.payload?.code == 200) {
                Swal.fire({
                    toast: true, position: 'top-end', icon: 'success', backdrop: false,
                    title: response?.payload?.message,
                    showConfirmButton: false, timer: 1500,
                })
            }
            refreshList()
        } catch (error) {
            console.error('Error toggling status:', error)
        }
    }

    // ── Action handlers ───────────────────────────────────────────────────────
    const handleToggleMohreStatus = async (tutorId, status) => {
        try {
            const response = await dispatch(changeTutorMohreStatus({ id: tutorId, is_mohre_verification: status === 1 ? 0 : 1 }))
            if (response?.payload?.code == 200) {
                Swal.fire({
                    toast: true, position: 'top-end', icon: 'success', backdrop: false,
                    title: response?.payload?.message,
                    showConfirmButton: false, timer: 1500,
                })
            }
            refreshList()
        } catch (error) {
            console.error('Error toggling status:', error)
        }
    }

    const handleDelete = async (tutorId) => {
        const result = await Swal.fire({
            title: 'Delete Tutor?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        })

        if (!result.isConfirmed) return

        try {
            const deleteResponse = await dispatch(deleteTutors({ id: tutorId }))
            if (deleteResponse?.payload?.code == 200) {
                Swal.fire({
                    icon: 'success', title: 'Deleted',
                    text: deleteResponse?.payload?.message,
                    timer: 1500, showConfirmButton: false,
                })
            }
            refreshList()
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Failed', text: error || 'Failed to delete tutor.' })
        }
    }

    const handleView = (tutorId) => navigate('/tutors/view', { state: { tutorId } })
    const handleEdit = (tutorId) => navigate('/tutors/edit', { state: { tutorId } })

    const handleApproveReject = async (tutorId, status) => {
        const isApprove = status === 'approved'

        const result = await Swal.fire({
            title: isApprove ? 'Approve Tutor?' : 'Reject Tutor?',
            text: isApprove
                ? 'This tutor will be approved and granted access.'
                : 'This tutor will be rejected.',
            icon: isApprove ? 'question' : 'warning',
            showCancelButton: true,
            confirmButtonColor: isApprove ? '#28a745' : '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: isApprove ? 'Yes, Approve' : 'Yes, Reject',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        })

        if (!result.isConfirmed) return

        try {
            const response = await dispatch(approveRejectTutors({ id: tutorId, profile_status: status }))
            if (response?.payload?.code === 200) {
                Swal.fire({
                    toast: true, position: 'top-end', icon: 'success', backdrop: false,
                    title: response?.payload?.message,
                    showConfirmButton: false, timer: 1500,
                })
                refreshList()
            }
        } catch (error) {
            Swal.fire({
                icon: 'error', title: 'Failed',
                text: error || `Failed to ${isApprove ? 'approve' : 'reject'} tutor.`,
            })
        }
    }

    const handleApprove = (tutorId) => handleApproveReject(tutorId, 'approved')
    const handleReject = (tutorId) => handleApproveReject(tutorId, 'rejected')

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns = [
        {
            name: 'Sr No',
            width: '80px',
            selector: (_, index) =>
                ((tutorList?.paging?.page_no || 1) - 1) * limit + index + 1,
        },
        {
            name: 'User ID',
            selector: row => row.id,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Full Name',
            selector: row => `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'NA',
            sortable: true,
            minWidth: '180px',
        },
        {
            name: 'Email',
            selector: row => row.email || 'NA',
            sortable: true,
            minWidth: '220px',
        },
        {
            name: 'Phone',
            selector: row =>
                row.country_code && row.phone_number
                    ? `${row.country_code}${row.phone_number}`
                    : 'NA',
            minWidth: '150px',
        },
        {
            name: 'DOB',
            selector: row => row.dob ? moment(row.dob).format('DD MMM YYYY') : 'NA',
            sortable: true,
            width: '130px',
        },
        {
            name: 'Joining Date',
            selector: row =>
                row.createdAt
                    ? moment(row.createdAt).local().format('DD MMM YYYY, hh:mm A')
                    : 'NA',
            sortable: true,
            minWidth: '200px',
        },
        {
            name: 'Profile',
            width: '100px',
            center: true,
            cell: row => (
                <img
                    src={row.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                    alt="profile"
                    onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                    }}
                    style={{
                        width: '36px', height: '36px',
                        borderRadius: '50%', objectFit: 'cover',
                    }}
                />
            ),
        },
        {
            name: 'Status',
            width: '100px',
            center: true,
            cell: row => (
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`switch-${row.id}`}
                        checked={row.is_active === 1}
                        onChange={() => handleToggleStatus(row.id)}
                        disabled={!hasPermission('tutors', 'edit') || row.is_deleted === 1}
                        style={{
                            cursor: hasPermission('tutors', 'edit') && row.is_deleted !== 1
                                ? 'pointer' : 'not-allowed',
                            opacity: row.is_deleted === 1 ? 0.5 : 1,
                        }}
                    />
                </div>
            ),
        },
        {
            name: 'MoHRE Permit Holder',
            width: '200px',
            center: true,
            cell: row => (
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`switch-${row.id}`}
                        checked={row.is_mohre_verification === 1}
                        onChange={() => handleToggleMohreStatus(row.id, row.is_mohre_verification)}
                        disabled={!hasPermission('tutors', 'edit') || row.is_deleted === 1}
                        style={{
                            cursor: hasPermission('tutors', 'edit') && row.is_deleted !== 1
                                ? 'pointer' : 'not-allowed',
                            opacity: row.is_deleted === 1 ? 0.5 : 1,
                        }}
                    />
                </div>
            ),
        },
        {
            name: 'Action',
            width: '120px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2 justify-content-center">
                    {hasPermission('tutors', 'view') && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="View"
                            onClick={() => handleView(row.id)}
                            style={{
                                width: '32px', height: '32px', padding: '0',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid #dee2e6', borderRadius: '4px',
                            }}
                        >
                            <i className="fe-eye" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                    {hasPermission('tutors', 'edit') && (
                        <button
                            className="btn btn-sm btn-outline-warning"
                            title="Edit"
                            onClick={() => handleEdit(row.id)}
                            style={{
                                width: '32px', height: '32px', padding: '0',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid #dee2e6', borderRadius: '4px',
                            }}
                        >
                            <i className="fe-edit" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                    {hasPermission('tutors', 'delete') && row.is_deleted !== 1 && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                            style={{
                                width: '32px', height: '32px', padding: '0',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid #dc3545', borderRadius: '4px',
                            }}
                        >
                            <i className="fe-trash-2" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                </div>
            ),
        },
        {
            name: 'Subscription',
            width: '180px',
            center: true,
            cell: row => {

                // not eligible if not approved
                if (row.profile_status !== 'approved') {
                    return (
                        <span style={{ fontSize: '11px', color: '#adb5bd', fontStyle: 'italic' }}>
                            Not Eligible
                        </span>
                    )
                }

                const canEdit = hasPermission('tutors', 'edit')
                const isActive = isTutorSubscribed(row)
                const isManual = isManualPlan(row)

                // ── Active plan ───────────────────────────────────────────────
                if (isActive) {
                    return (
                        <div className="d-flex flex-column align-items-center gap-1">

                            {/* Active badge */}
                            <span
                                className="badge"
                                style={{
                                    background: 'linear-gradient(135deg, #28a745, #20c997)',
                                    color: '#fff', padding: '5px 12px', fontSize: '11px',
                                    fontWeight: '600', borderRadius: '20px', letterSpacing: '0.3px',
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                }}
                            >
                                <i className="fe-check-circle" style={{ fontSize: '11px' }} />
                                Active Plan
                            </span>

                            {/* Plan type pill — Manual (blue) or IAP (amber) */}
                            <span style={{
                                fontSize: '10px', fontWeight: '500',
                                padding: '1px 8px', borderRadius: '8px',
                                background: isManual ? '#e0f2fe' : '#fef3c7',
                                color: isManual ? '#0369a1' : '#92400e',
                            }}>
                                {isManual ? 'Manual' : 'IAP'}
                            </span>

                            {/* Expiry date */}
                            <span style={{ fontSize: '10px', color: '#6c757d' }}>
                                Expires: {moment(row.subscription_end_date).format('DD MMM YYYY')}
                            </span>

                            {/* Edit Date — only enabled for manual plans */}
                            <button
                                className="btn btn-sm"
                                disabled={!canEdit || !isManual}
                                onClick={() => canEdit && isManual && openSubModal(row)}
                                title={
                                    !isManual
                                        ? 'IAP subscription dates are managed by Apple/Google'
                                        : 'Edit subscription end date'
                                }
                                style={{
                                    fontSize: '10px',
                                    padding: '2px 10px',
                                    borderRadius: '12px',
                                    lineHeight: '1.4',
                                    cursor: (canEdit && isManual) ? 'pointer' : 'default',

                                    // ── Manual → green outline button ──────────────────────
                                    ...(isManual ? {
                                        color: '#28a745',
                                        background: 'transparent',
                                        border: '1px solid #28a745',
                                        opacity: canEdit ? 1 : 0.5,
                                    } : {
                                        // ── IAP → solid amber/orange pill so it's clearly visible
                                        color: '#92400e',
                                        background: '#fef3c7',
                                        border: '1px solid #fbbf24',
                                        opacity: 1,
                                        fontWeight: '600',
                                    }),
                                }}
                            >
                                {isManual ? 'Edit Date' : 'IAP Plan'}
                            </button>
                        </div>
                    )
                }

                // ── No active plan — Assign button ────────────────────────────
                return (
                    <button
                        className="btn btn-sm"
                        disabled={!canEdit}
                        onClick={() => canEdit && openSubModal(row)}
                        style={{
                            background: canEdit
                                ? 'linear-gradient(135deg, #28a745, #20c997)'
                                : '#c3c3c3',
                            color: '#fff', border: 'none', borderRadius: '20px',
                            padding: '6px 16px', fontSize: '12px', fontWeight: '500',
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            cursor: canEdit ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s ease',
                            boxShadow: canEdit ? '0 2px 6px rgba(40,167,69,0.3)' : 'none',
                            opacity: canEdit ? 1 : 0.6,
                        }}
                        onMouseEnter={e => {
                            if (canEdit) {
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(40,167,69,0.45)'
                                e.currentTarget.style.transform = 'translateY(-1px)'
                            }
                        }}
                        onMouseLeave={e => {
                            if (canEdit) {
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(40,167,69,0.3)'
                                e.currentTarget.style.transform = 'translateY(0)'
                            }
                        }}
                    >
                        <i className="fe-plus-circle" style={{ fontSize: '13px' }} />
                        Assign Plan
                    </button>
                )
            },
        },
        {
            name: 'Approve / Reject',
            width: '140px',
            center: true,
            cell: row => {
                const canEdit = hasPermission('tutors', 'edit')

                if (row.profile_status === 'approved' || row.profile_status === 'rejected') {
                    return (
                        <span
                            className={`badge ${row.profile_status === 'approved' ? 'bg-success' : 'bg-danger'}`}
                            style={{ padding: '6px 10px', fontSize: '12px' }}
                        >
                            {row.profile_status.toUpperCase()}
                        </span>
                    )
                }

                return (
                    <div className="d-flex gap-2 justify-content-center">
                        <button
                            className="btn btn-sm btn-outline-success"
                            title="Approve"
                            disabled={!canEdit}
                            onClick={() => handleApprove(row.id)}
                            style={{
                                width: '32px', height: '32px', padding: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                opacity: canEdit ? 1 : 0.5,
                            }}
                        >
                            <i className="fe-check" style={{ fontSize: '14px' }} />
                        </button>
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Reject"
                            disabled={!canEdit}
                            onClick={() => handleReject(row.id)}
                            style={{
                                width: '32px', height: '32px', padding: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                opacity: canEdit ? 1 : 0.5,
                            }}
                        >
                            <i className="fe-x" style={{ fontSize: '14px' }} />
                        </button>
                    </div>
                )
            },
        },
    ]

    // ── Table styles ──────────────────────────────────────────────────────────
    const customStyles = {
        table: { style: { backgroundColor: '#ffffff', borderRadius: '8px' } },
        headRow: { style: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6', minHeight: '48px' } },
        headCells: { style: { fontSize: '13px', fontWeight: '600', color: '#495057', paddingLeft: '12px', paddingRight: '12px' } },
        cells: { style: { fontSize: '13px', color: '#212529', paddingTop: '12px', paddingBottom: '12px', paddingLeft: '12px', paddingRight: '12px' } },
        rows: {
            style: { minHeight: '60px', '&:hover': { backgroundColor: '#f8f9fa', cursor: 'pointer' } },
            stripedStyle: { backgroundColor: '#ffffff' },
        },
        pagination: { style: { borderTop: '1px solid #dee2e6', minHeight: '56px', fontSize: '13px' } },
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-semibold">Tutors</h4>
                {hasPermission('tutors', 'add') && (
                    <Link
                        to="/tutors/add"
                        className="btn btn-success"
                        style={{ padding: '8px 20px', fontSize: '14px', fontWeight: '500', borderRadius: '6px' }}
                    >
                        + Add Tutor
                    </Link>
                )}
            </div>

            {/* Filters */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="position-relative">
                                <i className="fe-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '16px' }} />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name, email, username, or phone..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{ paddingLeft: '38px', paddingRight: searchQuery ? '38px' : '12px', height: '40px', fontSize: '14px', border: '1px solid #dee2e6', borderRadius: '6px' }}
                                />
                                {searchQuery && (
                                    <button
                                        className="btn btn-link position-absolute p-0"
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
                                    >
                                        <i className="fe-x" style={{ fontSize: '16px' }} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="col-md-4">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                                style={{ height: '40px', fontSize: '14px', border: '1px solid #dee2e6', borderRadius: '6px' }}
                            >
                                <option value="all">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                                <option value="deleted">Deleted</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <DataTable
                    columns={columns}
                    data={tutorList?.data || []}
                    pagination
                    paginationServer
                    paginationTotalRows={tutorList?.paging?.total || 0}
                    paginationPerPage={limit}
                    paginationDefaultPage={tutorList?.paging?.page_no || 1}
                    onChangeRowsPerPage={(newLimit) => { setLimit(newLimit); setPage(1) }}
                    onChangePage={(newPage) => setPage(newPage)}
                    responsive
                    highlightOnHover
                    persistTableHead
                    progressPending={tutorList?.loading}
                    noDataComponent={
                        <div className="p-5 text-center">
                            <i className="fe-inbox" style={{ fontSize: '48px', color: '#adb5bd' }} />
                            <h6 className="mt-3 mb-1 text-muted">
                                {searchQuery ? 'No tutors found matching your search' : 'No tutors found'}
                            </h6>
                            <p className="text-muted small mb-0">
                                {searchQuery ? 'Try adjusting your search criteria' : 'Get started by adding a new tutor'}
                            </p>
                        </div>
                    }
                    customStyles={customStyles}
                />
            </div>

            {/* ── Subscription Modal ─────────────────────────────────────────── */}
            {showSubModal && (
                <div
                    onClick={closeSubModal}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                        zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        animation: 'fadeIn 0.2s ease',
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '460px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden',
                            animation: 'slideUp 0.3s ease',
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{ background: 'linear-gradient(135deg, #28a745, #20c997)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fe-calendar" style={{ color: '#fff', fontSize: '18px' }} />
                                </div>
                                <div>
                                    <h6 style={{ margin: 0, color: '#fff', fontWeight: '600', fontSize: '16px' }}>
                                        {isTutorSubscribed(selectedTutor) ? 'Update Subscription' : 'Assign Subscription'}
                                    </h6>
                                    <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px' }}>
                                        Set subscription end date
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={closeSubModal}
                                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                            >
                                <i className="fe-x" style={{ color: '#fff', fontSize: '16px' }} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '24px' }}>

                            {/* Tutor info card */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: '#f8f9fa', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                                <img
                                    src={selectedTutor?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                                    alt="tutor"
                                    onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png' }}
                                    style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e9ecef' }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#212529' }}>
                                        {`${selectedTutor?.first_name || ''} ${selectedTutor?.last_name || ''}`.trim() || 'NA'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                                        {selectedTutor?.email || 'No email'}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                    {/* Subscribed / No Plan badge */}
                                    <span style={{ fontSize: '11px', fontWeight: '500', padding: '3px 10px', borderRadius: '12px', background: isTutorSubscribed(selectedTutor) ? '#d1fae5' : '#fef3c7', color: isTutorSubscribed(selectedTutor) ? '#065f46' : '#92400e' }}>
                                        {isTutorSubscribed(selectedTutor) ? 'Subscribed' : 'No Plan'}
                                    </span>
                                    {/* Plan type badge — only when subscribed */}
                                    {isTutorSubscribed(selectedTutor) && (
                                        <span style={{ fontSize: '10px', fontWeight: '500', padding: '2px 8px', borderRadius: '8px', background: isManualPlan(selectedTutor) ? '#e0f2fe' : '#fef3c7', color: isManualPlan(selectedTutor) ? '#0369a1' : '#92400e' }}>
                                            {isManualPlan(selectedTutor) ? 'Manual' : 'IAP'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Current plan info */}
                            {isTutorSubscribed(selectedTutor) && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#ecfdf5', borderRadius: '10px', marginBottom: '20px', border: '1px solid #a7f3d0' }}>
                                    <i className="fe-info" style={{ color: '#059669', fontSize: '15px' }} />
                                    <span style={{ fontSize: '13px', color: '#065f46' }}>
                                        Current plan expires on{' '}
                                        <strong>{moment(selectedTutor?.subscription_end_date).format('DD MMM YYYY hh:mm A')}</strong>
                                    </span>
                                </div>
                            )}

                            {/* Date input */}
                            <div style={{ marginBottom: '8px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                    Subscription End Date <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    value={subEndDate}
                                    onChange={e => handleDateChange(e.target.value)}
                                    min={moment().format('YYYY-MM-DDTHH:mm')}
                                    style={{ width: '100%', padding: '10px 14px', fontSize: '14px', border: subDateError ? '1.5px solid #dc3545' : '1.5px solid #d1d5db', borderRadius: '10px', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s', background: '#fff' }}
                                    onFocus={e => {
                                        e.currentTarget.style.borderColor = subDateError ? '#dc3545' : '#28a745'
                                        e.currentTarget.style.boxShadow = subDateError ? '0 0 0 3px rgba(220,53,69,0.12)' : '0 0 0 3px rgba(40,167,69,0.12)'
                                    }}
                                    onBlur={e => {
                                        e.currentTarget.style.borderColor = subDateError ? '#dc3545' : '#d1d5db'
                                        e.currentTarget.style.boxShadow = 'none'
                                    }}
                                />
                                {subDateError && (
                                    <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>
                                        <i className="fe-alert-triangle" style={{ marginRight: '4px' }} />
                                        {subDateError}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#fafafa' }}>
                            <button
                                onClick={closeSubModal}
                                disabled={subLoading}
                                style={{ padding: '9px 20px', fontSize: '13px', fontWeight: '500', borderRadius: '10px', border: '1.5px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', transition: 'all 0.2s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#9ca3af' }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#d1d5db' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubscriptionSubmit}
                                disabled={subLoading || !subEndDate || !!subDateError}
                                style={{
                                    padding: '9px 24px', fontSize: '13px', fontWeight: '600', borderRadius: '10px', border: 'none',
                                    background: (!subEndDate || subLoading || !!subDateError) ? '#b7e4c7' : 'linear-gradient(135deg, #28a745, #20c997)',
                                    color: '#fff',
                                    cursor: (!subEndDate || subLoading || !!subDateError) ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    boxShadow: (!subEndDate || subLoading || !!subDateError) ? 'none' : '0 2px 8px rgba(40,167,69,0.35)',
                                }}
                                onMouseEnter={e => { if (subEndDate && !subLoading && !subDateError) e.currentTarget.style.boxShadow = '0 4px 14px rgba(40,167,69,0.5)' }}
                                onMouseLeave={e => { if (subEndDate && !subLoading && !subDateError) e.currentTarget.style.boxShadow = '0 2px 8px rgba(40,167,69,0.35)' }}
                            >
                                {subLoading && (
                                    <span className="spinner-border spinner-border-sm" role="status" style={{ width: '14px', height: '14px' }} />
                                )}
                                {isTutorSubscribed(selectedTutor) ? 'Update Plan' : 'Assign Plan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Animations */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0)     scale(1);    }
                }
            `}</style>
        </div>
    )
}

export default TutorList;